// useBackgroundTimers — surfaces the cook's still-running timers
// on steps OTHER THAN the active one. Drives the persistent
// "🍝 PASTA · 4:12 ←" floating banner in CookMode so the cook
// trusts that advancing past a passive step doesn't lose the
// timer.
//
// Reads from `cook_session_steps`:
//   - rows for this session (user's OWN — multi-helper rows are
//     out of scope here; the participants strip handles those)
//   - finished_at IS NULL  (timer is still alive)
//   - step_id !== activeStepId  (banner is about OTHER steps)
//   - timer_state.endsAt is in the future  (timer not yet rung)
//
// Subscribes via realtime so the row set tracks edits the
// telemetry writer makes elsewhere (pause / resume / end). The
// remaining-seconds figure is re-derived locally each render via
// a 1-second tick — no DB poll for the countdown.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabase";

export function useBackgroundTimers({ sessionId, userId, activeStepId }) {
  const [rows, setRows] = useState([]);
  // 1-second ticker so the rendered "4:12" countdown drops each
  // second without re-fetching. Cheap — just a setState in a small
  // hook scope, doesn't invalidate the parent tree's memos.
  const [tickMs, setTickMs] = useState(() => Date.now());

  // Subscribe to this session's step rows. Initial fetch hydrates
  // the row list; realtime keeps it fresh as the user (and helpers)
  // mutate timer_state via the writers in useCookTelemetry.
  useEffect(() => {
    if (!sessionId || !userId) {
      setRows([]);
      return undefined;
    }
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from("cook_session_steps")
        .select("id, step_id, step_title, finished_at, timer_state, user_id")
        .eq("cook_session_id", sessionId)
        .eq("user_id", userId)
        .is("finished_at", null);
      if (alive) setRows(data || []);
    })().catch((e) => {
      console.warn("[useBackgroundTimers] initial fetch failed:", e?.message || e);
    });

    // Realtime channel scoped to this session's rows so writes from
    // markTimerStarted / markTimerPaused / markTimerEnded reflect
    // here without a re-fetch. Channel name carries sessionId so
    // multiple cook sessions never collide.
    const ch = supabase
      .channel(`rt:cook_session_steps:bg:${sessionId}:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "cook_session_steps",
          filter: `cook_session_id=eq.${sessionId}`,
        },
        (payload) => {
          if (!alive) return;
          setRows((prev) => {
            const next = [...prev];
            const i = next.findIndex((r) => r.id === payload.new?.id || r.id === payload.old?.id);
            if (payload.eventType === "DELETE") {
              return i === -1 ? prev : next.filter((_, j) => j !== i);
            }
            if (payload.new) {
              if (i === -1) next.push(payload.new);
              else next[i] = payload.new;
            }
            return next;
          });
        },
      )
      .subscribe();

    return () => {
      alive = false;
      try { supabase.removeChannel(ch); } catch { /* noop */ }
    };
  }, [sessionId, userId]);

  // Local tick — every 1s so the rendered "4:12 → 4:11 → 4:10"
  // countdown updates without a re-fetch. Tied to a stable id so
  // strict-mode double-mount doesn't double-tick.
  useEffect(() => {
    const id = setInterval(() => setTickMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Derive the visible list. Filter to rows where the timer is
  // still ticking (endsAt in the future, NOT paused, NOT ended)
  // AND the step isn't the active one (active step's timer renders
  // inline; banner is for elsewhere).
  return useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const out = [];
    for (const r of rows) {
      if (String(r.step_id) === String(activeStepId)) continue;
      const ts = r.timer_state;
      if (!ts || typeof ts !== "object") continue;
      if (ts.ended || ts.isPaused) continue;
      const endsAt = ts.endsAt ? new Date(ts.endsAt).getTime() : null;
      if (!endsAt || !Number.isFinite(endsAt)) continue;
      const remainingMs = endsAt - tickMs;
      if (remainingMs <= 0) continue;
      out.push({
        stepRowId: r.id,
        stepId: String(r.step_id),
        stepTitle: r.step_title || `Step ${r.step_id}`,
        secondsRemaining: Math.max(0, Math.round(remainingMs / 1000)),
      });
    }
    // Sort by least-remaining-first so the most-urgent timer leads.
    out.sort((a, b) => a.secondsRemaining - b.secondsRemaining);
    return out;
  }, [rows, activeStepId, tickMs]);
}
